import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  BlogContainer,
  BlogWrapper,
  BlogH1,
  BlogCard,
  BlogImage,
  BlogTitle,
  BlogDescription,
  BlogFooter,
  BlogAuthor,
  Avatar,
  BlogMeta,
  LikeButton,
  CommentLink,
  CommentForm,
  NameInput,
  CommentInput,
  CommentButton,
  CommentList,
  Comment,
  CommentText,
  CommentAvatar,
  CommentAuthor,
  CommentDate,
  Loading,
  Error,
  NoBlogsMessage,
} from "./BlogElements";

const BlogPost = ({ blog }) => {
  const navigate = useNavigate();
  const [likes, setLikes] = useState(blog.likeCount || 0);
  const [isLiked, setIsLiked] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [userName, setUserName] = useState("");
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const baseUrl = process.env.REACT_APP_BASE_URL;

  // Ref for the comment section to detect outside clicks
  const commentSectionRef = useRef(null);

  // Check if user liked this blog
  useEffect(() => {
    const checkLikeStatus = async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/auth/blogs/${blog.id}/like-status`,
        );
        setIsLiked(response.data.liked);
      } catch (error) {
        console.error("Error checking like status:", error);
      }
    };

    checkLikeStatus();
  }, [blog.id, baseUrl]);

  // Load comments
  useEffect(() => {
    const loadComments = async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/auth/blogs/${blog.id}/comments`,
        );
        setComments(response.data);
      } catch (error) {
        console.error("Error loading comments:", error);
      }
    };

    loadComments();
  }, [blog.id, baseUrl]);

  // Handle click outside to close comment section
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showCommentForm &&
        commentSectionRef.current &&
        !commentSectionRef.current.contains(event.target) &&
        !event.target.closest(".comment-link")
      ) {
        setShowCommentForm(false);
      }
    };

    if (showCommentForm) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCommentForm]);

  const handleLike = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${baseUrl}/auth/like`, {
        blogId: blog.id,
      });

      if (response.data.liked) {
        setLikes(response.data.likeCount || likes + 1);
        setIsLiked(true);
      } else {
        setLikes(response.data.likeCount || likes - 1);
        setIsLiked(false);
      }
    } catch (error) {
      console.error("Error toggling like:", error);
      alert("Failed to like post");
    } finally {
      setLoading(false);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setLoading(true);
    try {
      const response = await axios.post(`${baseUrl}/auth/comment`, {
        blogId: blog.id,
        text: newComment,
        userName: userName.trim() || "Anonymous",
      });

      setComments([response.data, ...comments]);
      setNewComment("");
      setUserName("");
    } catch (error) {
      console.error("Error adding comment:", error);
      alert("Failed to add comment");
    } finally {
      setLoading(false);
    }
  };

  const toggleCommentForm = () => {
    setShowCommentForm(!showCommentForm);
    if (!showCommentForm) {
      setUserName("");
    }
  };

  const truncatedDescription =
    blog.description.length > 100
      ? blog.description.substring(0, 100) + "..."
      : blog.description;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCommentDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "1 day ago";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return formatDate(dateString);
  };

  const handleBlogClick = () => {
    navigate(`/blog/${blog.id}`);
  };

  return (
    <BlogCard>
      <BlogImage
        src={`${baseUrl}${blog.imageUrl}`}
        alt={blog.title}
        onClick={handleBlogClick}
        style={{ cursor: "pointer" }}
        onError={(e) => {
          e.target.src =
            "https://via.placeholder.com/300x160/4B5563/FFFFFF?text=Blog+Image";
        }}
      />
      <BlogTitle onClick={handleBlogClick} style={{ cursor: "pointer" }}>
        {blog.title}
      </BlogTitle>
      <BlogDescription>{truncatedDescription}</BlogDescription>
      <BlogFooter>
        <BlogAuthor>
          <Avatar
            src="https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=crop&w=1061&q=80"
            alt={blog.author}
          />
          <div>
            <div
              style={{
                fontSize: "0.8rem",
                fontWeight: "bold",
                color: "#1f2937",
              }}
            >
              {blog.author}
            </div>
            <BlogMeta>
              <span>{formatDate(blog.date)}</span>
            </BlogMeta>
          </div>
        </BlogAuthor>
        <BlogMeta>
          <LikeButton
            liked={isLiked}
            onClick={handleLike}
            disabled={loading}
            title={isLiked ? "Unlike this post" : "Like this post"}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
            {likes} {likes === 1 ? "Like" : "Likes"}
          </LikeButton>
          <CommentLink
            onClick={toggleCommentForm}
            title="View comments"
            className="comment-link"
          >
            {comments.length} {comments.length === 1 ? "Comment" : "Comments"}
          </CommentLink>
        </BlogMeta>

        {showCommentForm && (
          <div ref={commentSectionRef}>
            <CommentForm onSubmit={handleCommentSubmit}>
              <div>
                <NameInput
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="Your name (optional)"
                  disabled={loading}
                />
              </div>
              <CommentInput
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Share your thoughts..."
                disabled={loading}
              />
              <CommentButton
                type="submit"
                disabled={loading || !newComment.trim()}
              >
                {loading ? "Posting..." : "Post Comment"}
              </CommentButton>
            </CommentForm>

            {comments.length > 0 && (
              <CommentList>
                {comments.map((comment) => (
                  <Comment key={comment.id}>
                    <CommentAvatar
                      src={comment.userAvatar}
                      alt={comment.userName}
                      onError={(e) => {
                        e.target.src =
                          "https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=crop&w=1061&q=80";
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <CommentAuthor>{comment.userName}</CommentAuthor>
                      <CommentText>{comment.text}</CommentText>
                      <CommentDate>
                        {formatCommentDate(comment.createdAt)}
                      </CommentDate>
                    </div>
                  </Comment>
                ))}
              </CommentList>
            )}
          </div>
        )}
      </BlogFooter>
    </BlogCard>
  );
};

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleRows, setVisibleRows] = useState(1); // Start with 1 row
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const ITEMS_PER_ROW = 3; // Number of blog posts per row

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${baseUrl}/auth/blogs`);
        setBlogs(response.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching blogs:", err);
        setError("Failed to load blogs. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, [baseUrl]);

  const handleViewMore = () => {
    setVisibleRows(prevRows => prevRows + 1);
  };

  // Calculate how many blogs to show
  const visibleBlogsCount = visibleRows * ITEMS_PER_ROW;
  const visibleBlogs = blogs.slice(0, visibleBlogsCount);
  const hasMoreBlogs = visibleBlogsCount < blogs.length;

  if (loading) {
    return (
      <BlogContainer id="blog">
        <BlogH1>Our Blog</BlogH1>
        <BlogWrapper>
          <Loading>Loading blogs...</Loading>
        </BlogWrapper>
      </BlogContainer>
    );
  }

  if (error) {
    return (
      <BlogContainer id="blog">
        <BlogH1>Our Blog</BlogH1>
        <BlogWrapper>
          <Error>
            {error}
            <br />
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: "10px",
                padding: "8px 16px",
                backgroundColor: "#010606",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              Try Again
            </button>
          </Error>
        </BlogWrapper>
      </BlogContainer>
    );
  }

  return (
    <BlogContainer id="blog">
      <BlogH1>Our Blog</BlogH1>
      <BlogWrapper>
        {blogs.length === 0 ? (
          <NoBlogsMessage>
            No blog posts available yet.
            <br />
            <span style={{ fontSize: "0.9rem", color: "#9ca3af" }}>
              Check back later for new content!
            </span>
          </NoBlogsMessage>
        ) : (
          visibleBlogs.map((blog) => <BlogPost key={blog.id} blog={blog} />)
        )}
      </BlogWrapper>

      {/* View More Button */}
      {hasMoreBlogs && (
        <div style={{ marginTop: "32px", textAlign: "center" }}>
          <button
            onClick={handleViewMore}
            style={{
              padding: "12px 32px",
              backgroundColor: "#010606",
              color: "white",
              border: "none",
              borderRadius: "50px",
              cursor: "pointer",
              fontSize: "1rem",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#1976d3";
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 12px rgba(0, 0, 0, 0.15)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#010606";
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)";
            }}
          >
            View More
          </button>
        </div>
      )}
    </BlogContainer>
  );
};

export default Blog;
